<template>
  <div class="payment-gateways-page" data-testid="payment-gateways-page">
    <page-header
      :title="t('Gateways de Pagamento')"
      icon="pi pi-server"
    />

    <Tabs :value="0" :scrollable="false">
      <TabList>
        <Tab :value="0" data-testid="payment-gateways-tab-config">{{ t('Configuração') }}</Tab>
        <Tab
          v-if="isGatewayTestEnabled"
          :value="1"
          data-testid="payment-gateways-tab-gatewaytest"
        >
          {{ t('Gateway Teste') }}
        </Tab>
      </TabList>

      <TabPanels>
        <TabPanel :value="0" data-testid="payment-gateways-panel-config">

    <!-- Feature × Provider enablement matrix -->
    <box data-testid="payment-gateways-matrix">
      <template #header>{{ t('Matriz de habilitação') }}</template>

      <div v-if="isLoading" class="loading-state">
        <ProgressSpinner />
      </div>

      <div v-else-if="featureMap" class="matrix-table-wrapper">
        <table class="matrix-table">
          <thead>
            <tr>
              <th class="feature-col">{{ t('Funcionalidade') }}</th>
              <th
                v-for="provider in knownProviders"
                :key="provider"
                class="provider-col"
              >
                {{ providerLabel(provider) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(providerMap, feature) in featureMap"
              :key="feature"
              :data-testid="`gateway-feature-row-${feature}`"
            >
              <td class="feature-name">{{ featureLabel(feature) }}</td>
              <td
                v-for="provider in knownProviders"
                :key="provider"
                class="toggle-cell"
              >
                <input-toggle
                  v-if="providerMap[provider] !== undefined"
                  :model-value="providerMap[provider].enabled"
                  :elem-id="`gateway-toggle-${feature}-${provider}`"
                  :data-testid="`gateway-toggle-${feature}-${provider}`"
                  @update:model-value="(val: boolean) => handleToggle(feature, provider, val)"
                />
                <span v-else class="not-available">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="empty-state">
        <p>{{ t('Nenhum gateway configurado.') }}</p>
      </div>
    </box>

    <!-- Per-provider capabilities display (read-only) -->
    <box
      v-if="featureMap"
      data-testid="payment-gateways-capabilities"
      class="capabilities-section"
    >
      <template #header>{{ t('Capacidades declaradas por provedor') }}</template>

      <div class="capabilities-grid">
        <div
          v-for="provider in knownProviders"
          :key="provider"
          class="provider-capabilities"
          :data-testid="`gateway-capabilities-${provider}`"
        >
          <h4 class="provider-title">{{ providerLabel(provider) }}</h4>
          <div class="capability-chips">
            <template v-if="providerCapabilities(provider)">
              <span
                v-for="(supported, cap) in providerCapabilities(provider)"
                :key="cap"
                class="capability-chip"
                :class="supported ? 'chip-supported' : 'chip-unsupported'"
              >
                <i :class="supported ? 'pi pi-check' : 'pi pi-times'" />
                {{ cap }}
              </span>
            </template>
            <span v-else class="no-capabilities">{{ t('Sem capacidades declaradas') }}</span>
          </div>
        </div>
      </div>
    </box>
        </TabPanel>

        <!-- GatewayTest console tab — the Tab above and the component below use
             the SAME enabled-flag signal (presence alone means nothing: the row
             is seeded everywhere). The component keeps its own internal gate
             too, so rendering it elsewhere stays safe. -->
        <TabPanel v-if="isGatewayTestEnabled" :value="1" data-testid="payment-gateways-panel-gatewaytest">
          <GatewayTestDevTrigger />
        </TabPanel>
      </TabPanels>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { usePaymentGatewayRegistry } from '../../../composables/usePaymentGatewayRegistry'
import PageHeader from '../../../ui/PageHeader.vue'
import GatewayTestDevTrigger from './GatewayTestDevTrigger.vue'

const { t } = useI18n()
const { featureMap, isLoading, loadMap, toggle } = usePaymentGatewayRegistry()

// The provider columns are DERIVED from the map the backend returns — never a
// hardcoded list. A provider registered server-side (asaas at P4, or any future
// gateway) shows up here the moment it appears in the map. Sorted so the column
// order is deterministic across reloads.
// Same enabled-flag signal GatewayTestDevTrigger gates on internally — the
// "Gateway Teste" tab must not exist in environments where the simulator is
// off (production seeds the row; only the flag means enabled).
const isGatewayTestEnabled = computed<boolean>(() => {
  if (!featureMap.value) return false
  return Object.values(featureMap.value).some(providerMap => providerMap['gatewaytest']?.enabled === true)
})

const knownProviders = computed<string[]>(() => {
  if (!featureMap.value) return []
  const seen = new Set<string>()
  for (const providerMap of Object.values(featureMap.value)) {
    for (const code of Object.keys(providerMap)) seen.add(code)
  }
  return Array.from(seen).sort()
})

function providerLabel(provider: string): string {
  const labels: Record<string, string> = {
    gatewaytest: t('GatewayTest (dev)'),
    stripe: 'Stripe',
    asaas: 'ASAAS',
  }
  // Unknown/legacy providers surface by their raw code — honest over invented.
  return labels[provider] ?? provider
}

function featureLabel(feature: string): string {
  const labels: Record<string, string> = {
    subscriptions: t('Assinaturas'),
    ai_credit_topup: t('Recarga de Créditos IA'),
    setup_fee: t('Taxa de Implantação'),
  }
  return labels[feature] ?? feature
}


/**
 * Returns the capabilities map for the first feature that has an entry for
 * this provider (capabilities are code-declared and identical across features
 * for the same provider).
 */
function providerCapabilities(provider: string): Record<string, boolean> | null {
  if (!featureMap.value) return null
  for (const providerMap of Object.values(featureMap.value)) {
    if (providerMap[provider]?.capabilities) {
      return providerMap[provider].capabilities
    }
  }
  return null
}

async function handleToggle(feature: string, provider: string, enabled: boolean): Promise<void> {
  try {
    await toggle(feature, provider, enabled)
  } catch (err) {
    log('PaymentGatewaysPage', 'handleToggle', 'Erro ao alternar gateway', err)
    eventBus.emit('globalMessage.error', t('Erro ao alterar configuração do gateway.'))
  }
}

onMounted(async () => {
  try {
    await loadMap()
  } catch (err) {
    log('PaymentGatewaysPage', 'onMounted', 'Erro ao carregar mapa de gateways', err)
    eventBus.emit('globalMessage.error', t('Erro ao carregar gateways.'))
  }
})
</script>

<style scoped>
.payment-gateways-page {
  padding: 24px;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 32px;
}

.matrix-table-wrapper {
  overflow-x: auto;
}

.matrix-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.matrix-table th,
.matrix-table td {
  padding: 10px 16px;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
}

.matrix-table th {
  font-weight: 600;
  background: #f9fafb;
  color: #374151;
}

.feature-col {
  min-width: 180px;
}

.provider-col {
  min-width: 120px;
  text-align: center;
}

.toggle-cell {
  text-align: center;
}

.feature-name {
  font-weight: 500;
  color: #111827;
}

.not-available {
  color: #9ca3af;
}

.empty-state {
  color: #6b7280;
  font-size: 0.9rem;
}

.capabilities-section {
  margin-top: 24px;
}

.capabilities-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}

.provider-capabilities {
  min-width: 200px;
}

.provider-title {
  margin: 0 0 10px 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #111827;
}

.capability-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.capability-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.78rem;
  font-weight: 500;
}

.chip-supported {
  background: #d1fae5;
  color: #065f46;
}

.chip-unsupported {
  background: #fee2e2;
  color: #991b1b;
}

.no-capabilities {
  color: #9ca3af;
  font-size: 0.85rem;
}
</style>
